import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import 'material-symbols/outlined.css';
import EmptyList from '../../../common/empty-list/empty-list.component';
import Button from '../../../common/button/button.component';
import InputMessage from '../../../common/input-message/input-message.component';
import InputSearch from '../../../common/input-search/input-search.component';
import {
  Content,
  PopularLabel,
} from '../../../common/input-search-with-sidebar/input-search-with-sidebar.styles';
import MenuItem, {
  MenuItemAppearance,
} from '../../../common/menu-item/menu-item.component';
import { MenuItems } from '../../../common/menu-item/menu-item.styles';
import Sidebar from '../../../common/sidebar-right/sidebar.component';
import { ReactComponent as ArrowRightIcon } from '../../../assets/arrow-right-icon.svg';
import { ReactComponent as ChevronRightIcon } from '../../../assets/chevron-right-icon.svg';
import {
  GatewayCategoryDto,
  GatewayCategoryFilterMode,
  GatewayCategoryPageRequestDto,
  getCategoryProhibitedCountries,
  getCategoryProhibitedShippingMethods,
  isCategoryFullyProhibited,
  isCategoryProhibitedForCountry,
  isCategoryProhibitedForShippingMethod,
} from '../../../models/gateway-category';
import { HsCodeResponse } from '../../../models/hsCode';
import type { CategoryPageUpdateMode } from '../../../stores/categoryStore';
import { store, useStore } from '../../../stores/store';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import {
  GatewayCategorySearchSidebarContainer,
  MaterialCategoryIcon,
} from './gateway-category-search.styles';
import { InputSearchContainer } from '../../../common/input-search/input-search.styles';
import { capitalizeFirstLetter } from '../../../utils/generic/generic.utils';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { translate } from '../../../../i18n';

import ParcelItemsAiLoadingOverlay from './parcel-items-ai-assistant/parcel-items-ai-loading-overlay.component';
import ParcelItemsAiBetaPanel from './parcel-items-ai-assistant/parcel-items-ai-beta-panel.component';
import ParcelItemsAiControls from './parcel-items-ai-assistant/parcel-items-ai-controls.component';
import { useAiRecognitionPanel } from './parcel-items-ai-assistant/use-ai-recognition-panel.hook';

const SIDEBAR_NAME = 'category_search';
const CATEGORY_PAGE = 0;
const CATEGORY_PAGE_LIMIT = 1000;

interface CategoryFormValues {
  category?: string;
  foundHsCode?: HsCodeResponse;
  categoryElement?: GatewayCategoryDto;
}

const getCategoryIcon = (icon: string | null) => {
  const iconName = icon?.trim();

  return iconName ? (
    <MaterialCategoryIcon>{iconName}</MaterialCategoryIcon>
  ) : undefined;
};

const getCategoryAppearance = (
  category?: GatewayCategoryDto
): MenuItemAppearance => {
  if (!category || !category.prohibited) return 'default';

  if (isCategoryFullyProhibited(category.prohibitedRules)) return 'danger';

  if (isCategoryProhibitedForShippingMethod(category.prohibitedRules))
    return 'warning';

  if (isCategoryProhibitedForCountry(category.prohibitedRules))
    return 'warning';

  return 'default';
};

const getShippingMethodLabel = (method: string) => {
  if (method === 'SEA') {
    return translate('parcel_item_category_delivery_method_sea');
  }
  if (method === 'CAR' || method === 'LAND') {
    return translate('parcel_item_category_delivery_method_car');
  }
  return translate('parcel_item_category_delivery_method_avia');
};

const getProhibitedText = (category?: GatewayCategoryDto) => {
  if (!category) return;

  const appearence = getCategoryAppearance(category);

  if (appearence === 'danger') {
    return capitalizeFirstLetter(translate('prohibited_for_delivery'));
  }

  const { prohibitedRules } = category;

  if (appearence === 'warning') {
    let prohibitedReasonLabels: string = '';

    if (isCategoryProhibitedForShippingMethod(prohibitedRules)) {
      const prohibitedMethods =
        getCategoryProhibitedShippingMethods(prohibitedRules);

      prohibitedReasonLabels = Array.from(
        new Set(prohibitedMethods.map(getShippingMethodLabel))
      ).join('/');
    }

    if (isCategoryProhibitedForCountry(prohibitedRules)) {
      const prohibitedCountryCodes =
        getCategoryProhibitedCountries(prohibitedRules);

      prohibitedReasonLabels = prohibitedCountryCodes
        .map((x) => store.shipmentCountriesStore.tryGetCountryLocalizedLabel(x))
        .join('/');
    }

    return translate('parcel_item_category_prohibited_reason', {
      prohibitedList: prohibitedReasonLabels,
    });
  }
};

function mapDeliveryMethod(method: DeliveryMethod | undefined) {
  if (method === 'sea') return 'SEA';
  if (method === 'car') return 'LAND';
  return 'AIR';
}

const GatewayCategorySearch = () => {
  const { getFieldProps, setValues, values } =
    useFormikContext<CategoryFormValues>();
  const {
    categoryStore: {
      canNavigateBack,
      categoriesPage,
      currentParentId,
      currentPageTitle,
      filterText,
      getCategoriesPage,
      isLoadingCategoriesPage,
      navigateBack,
      restoreCurrentNavigationPage,
      reset,
      setFilterText,
    },
    navStore: { toggleSidebarByName },
    parcelCreationStore: {
      countryDestination,
      isNextButtonClicked,
      selectedDeliveryOption,
      setIsNextButtonClicked,
      shipment,
    },
  } = useStore();

  const [isTouched, setIsTouched] = useState(false);
  const { t } = useTranslation();
  const {
    loadingMode,
    showBetaPanel,
    dismissBetaPanel,
    showAiControls,
    canRecordVoice,
    isAiAssistantDisabled,
    aiDisabledReason,
    handleScan,
    handleSpeak,
    searchFieldClassName,
  } = useAiRecognitionPanel(SIDEBAR_NAME);

  const destination = countryDestination?.code ?? '';
  const shippingMethod = mapDeliveryMethod(
    selectedDeliveryOption?.middle_mile.delivery_method
  );
  const inputValue = getFieldProps('category').value ?? '';
  const triggerValidation =
    isNextButtonClicked.value && !shipment?.items?.length;
  const showValidationError = (isTouched || triggerValidation) && !inputValue;

  const selectedCategoryAppearence = getCategoryAppearance(
    values.categoryElement
  );

  const showProhibitedError = selectedCategoryAppearence === 'danger';

  const showProhibitedWarning = selectedCategoryAppearence === 'warning';

  const loadCategoriesPage = useCallback(
    async (
      parentId: number | null,
      mode: GatewayCategoryFilterMode,
      text: string = '',
      updateMode: CategoryPageUpdateMode = 'replace',
      pageTitle?: string
    ) => {
      if (!destination || !shippingMethod) return undefined;

      const request: GatewayCategoryPageRequestDto = {
        page: CATEGORY_PAGE,
        limit: CATEGORY_PAGE_LIMIT,
        useAi: true,
        destination,
        shippingMethod,
        sortBy: 'NAME',
        filter: {
          text,
          warehouseId: null,
          mode,
          parentId,
        },
      };

      return getCategoriesPage(request, updateMode, pageTitle);
    },
    [destination, getCategoriesPage, shippingMethod]
  );

  useEffect(() => {
    loadCategoriesPage(null, 'TREE', '', 'initialize');
  }, [loadCategoriesPage]);

  const resetCategoriesPage = useCallback(() => {
    reset();
    loadCategoriesPage(null, 'TREE', '', 'initialize');
  }, [loadCategoriesPage, reset]);

  const handleSidebarSearch = async (text: string) => {
    setFilterText(text);

    if (!text) {
      restoreCurrentNavigationPage();
      return;
    }

    await loadCategoriesPage(currentParentId, 'CATEGORY', text);
  };

  const handleCategorySelected = async (category: GatewayCategoryDto) => {
    const mode: GatewayCategoryFilterMode = category.categoryGroup
      ? 'TREE'
      : 'CATEGORY';

    if (mode === 'CATEGORY') {
      const showImei = category.requirements.some(
        (x) => x.name === 'IMEI1' && x.destinationCounties.includes(destination)
      );
      const foundHsCode: HsCodeResponse = {
        hsCode: category.hsCode,
        category_id: category.id.toString(),
        description: category.name,
        descriptionEn: category.nameEn,
        additional_fields: showImei ? ['imei'] : undefined,
      };

      setValues(
        {
          ...values,
          categoryElement: category,
          category: category.name,
          foundHsCode,
        },
        true
      );
      setIsNextButtonClicked(false);
      setIsTouched(false);
      resetCategoriesPage();
      toggleSidebarByName(SIDEBAR_NAME);

      return;
    }

    await loadCategoriesPage(
      category.id,
      mode,
      '',
      category.categoryGroup ? 'forward' : 'replace',
      category.categoryGroup ? category.name : undefined
    );
  };

  const categories = categoriesPage?.elements ?? [];

  return (
    <InputSearchContainer $mb='0'>
      <InputSearch
        name={SIDEBAR_NAME}
        inputValue={inputValue}
        placeholder={t('choose_category')}
        label={t('item_category')}
        readOnly
        autoComplete='off'
        type='text'
        searchIconPosition='right'
        treatWholeInputAsSearch
        InputIcon={ArrowRightIcon}
        isError={showValidationError || showProhibitedError}
        isWarning={showProhibitedWarning}
        inputStyle={{ cursor: 'pointer' }}
        sidebarName={SIDEBAR_NAME}
        onInputContainerClick={() => {
          restoreCurrentNavigationPage();
          toggleSidebarByName(SIDEBAR_NAME);
        }}
      />

      <Sidebar
        name={SIDEBAR_NAME}
        withBlurredBackground
        header={t('category')}
        onClose={() => {
          setIsTouched(true);
          resetCategoriesPage();
        }}>
        <GatewayCategorySearchSidebarContainer>
          <div className='category-search-header'>
            {showBetaPanel && (
              <ParcelItemsAiBetaPanel onDismiss={dismissBetaPanel} />
            )}
            <div className={searchFieldClassName}>
              <InputSearch
                name={`search_${SIDEBAR_NAME}`}
                placeholder={t('parcel_item_category_search_placeholder')}
                focusOnOpen
                searchIconPosition='left'
                disableSearchIconHover
                showClearInputButton
                debounceInputChange
                debouncTime={550}
                onChange={handleSidebarSearch}
                onClear={() => handleSidebarSearch('')}
                isLoading={isLoadingCategoriesPage}
              />
              {showAiControls && (
                <ParcelItemsAiControls
                  canRecordVoice={canRecordVoice}
                  disabled={isAiAssistantDisabled}
                  disabledReason={aiDisabledReason}
                  onSpeak={handleSpeak}
                  onScan={handleScan}
                />
              )}
            </div>
          </div>
          <Content>
            <FlexContainer
              $direction='column'
              $align='start'
              $gap='1.6rem'>
              {canNavigateBack && !filterText && (
                <Button
                  $pl='8px'
                  size='mini'
                  appearance='text'
                  icon={{
                    glyph: 'chevron-left',
                    color: 'primary',
                    position: 'left',
                  }}
                  disabled={isLoadingCategoriesPage}
                  justifyContent='flex-start'
                  onClick={() => navigateBack()}>
                  {t('back')}
                </Button>
              )}
              {currentPageTitle && (
                <PopularLabel>{currentPageTitle}</PopularLabel>
              )}
            </FlexContainer>

            {categories.length ? (
              <MenuItems>
                {categories.map((category) => (
                  <MenuItem
                    key={category.id}
                    appearance={getCategoryAppearance(category)}
                    prohibitionReason={getProhibitedText(category)}
                    prefix={getCategoryIcon(category.icon)}
                    useFlagEmojiFont={false}
                    suffix={
                      category.categoryGroup ? <ChevronRightIcon /> : undefined
                    }
                    isDisabled={isLoadingCategoriesPage || category.prohibited}
                    onClick={() =>
                      !isLoadingCategoriesPage &&
                      handleCategorySelected(category)
                    }>
                    {category.name}
                  </MenuItem>
                ))}
              </MenuItems>
            ) : (
              !isLoadingCategoriesPage && (
                <EmptyList
                  isSidebar
                  imageType='loupe'
                  title={filterText ? t('no_search_results') : undefined}
                  description={
                    filterText
                      ? t('please_try_again')
                      : t('havent_searched_yet')
                  }
                />
              )
            )}
          </Content>
        </GatewayCategorySearchSidebarContainer>
      </Sidebar>

      {loadingMode && <ParcelItemsAiLoadingOverlay mode={loadingMode} />}

      {showProhibitedError && (
        <InputMessage type='error'>
          {t('parcel_item_category_prohibited')}
        </InputMessage>
      )}
      {!showProhibitedError && showValidationError && (
        <InputMessage type='error'>
          {t('this_field_cannot_be_empty')}
        </InputMessage>
      )}
      {showProhibitedWarning && (
        <InputMessage type='alertTriangleYellow'>
          {getProhibitedText(values.categoryElement)}
        </InputMessage>
      )}
    </InputSearchContainer>
  );
};

export default observer(GatewayCategorySearch);
